/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useRef} from 'react'
import {noop} from '../../utils/utils'
import {
    Button,
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogOverlay,
    Text,
    AlertDialogCloseButton
} from '@chakra-ui/react'

import PropTypes from 'prop-types'
import {CONFIRMATION_DIALOG_DEFAULT_CONFIG} from '../../constants'
import {useIntl} from 'react-intl'

const ConfirmationModal = ({
    dialogTitle = CONFIRMATION_DIALOG_DEFAULT_CONFIG.dialogTitle,
    confirmationMessage = CONFIRMATION_DIALOG_DEFAULT_CONFIG.confirmationMessage,
    primaryActionLabel = CONFIRMATION_DIALOG_DEFAULT_CONFIG.primaryActionLabel,
    primaryActionAriaLabel = CONFIRMATION_DIALOG_DEFAULT_CONFIG.primaryActionAriaLabel,
    alternateActionLabel = CONFIRMATION_DIALOG_DEFAULT_CONFIG.alternateActionLabel,
    alternateActionAriaLabel = CONFIRMATION_DIALOG_DEFAULT_CONFIG.alternateActionAriaLabel,
    body,
    onPrimaryAction = noop,
    onAlternateAction = noop,
    ...props
}) => {
    const {formatMessage} = useIntl()
    const alternateActionRef = useRef()

    const handleConfirmClick = () => {
        onPrimaryAction()
        props.onClose?.()
    }

    const handleAlternateActionClick = () => {
        onAlternateAction()
        props.onClose?.()
    }

    return (
        <AlertDialog
            isCentered
            onClose={handleAlternateActionClick}
            variant="confirm"
            leastDestructiveRef={alternateActionRef}
            {...props}
        >
            <AlertDialogOverlay />
            <AlertDialogContent>
                <AlertDialogHeader as="h1">{formatMessage(dialogTitle)}</AlertDialogHeader>
                <AlertDialogCloseButton />
                <AlertDialogBody>
                    {body ? body : <Text>{formatMessage(confirmationMessage)}</Text>}
                </AlertDialogBody>

                <AlertDialogFooter>
                    <Button
                        ref={alternateActionRef}
                        variant="link"
                        {...(alternateActionAriaLabel && {
                            'aria-label': formatMessage(alternateActionAriaLabel)
                        })}
                        onClick={handleAlternateActionClick}
                    >
                        {formatMessage(alternateActionLabel)}
                    </Button>
                    <Button
                        variant="solid"
                        width="full"
                        {...(primaryActionAriaLabel && {
                            'aria-label': formatMessage(primaryActionAriaLabel)
                        })}
                        onClick={handleConfirmClick}
                    >
                        {formatMessage(primaryActionLabel)}
                    </Button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    )
}

ConfirmationModal.propTypes = {
    /**
     * Prop to check if modal is open
     */
    isOpen: PropTypes.bool,
    /**
     * Callback invoked to open the modal
     */
    onOpen: PropTypes.func,
    /**
     * Callback invoked to close the modal
     */
    onClose: PropTypes.func,
    /**
     * Text to be displayed as modal header
     */
    dialogTitle: PropTypes.object,
    /**
     * Text to display in confirmation modal prompting user to pick an action
     */
    confirmationMessage: PropTypes.object,
    /**
     * Button Label for primary action in confirmation modal
     */
    primaryActionLabel: PropTypes.object,
    /**
     * Button aria Label for primary action
     */
    primaryActionAriaLabel: PropTypes.object,
    /**
     * Button Label for alternate or secondary action in confirmation modal
     */
    alternateActionLabel: PropTypes.object,
    /**
     * Button aria Label for alternate or secondary action in confirmation modal
     */
    alternateActionAriaLabel: PropTypes.object,
    /**
     * Body for confirmation modal
     */
    body: PropTypes.node,
    /**
     * Action to execute if user selects primary action
     */
    onPrimaryAction: PropTypes.func,
    /**
     * Action to execute if user selects alternate or secondary action
     */
    onAlternateAction: PropTypes.func
}

export default ConfirmationModal
